import {
  BookingType,
  type PhoneSupportAvailability,
} from "@b2bportal/core-types";
import { DateTimeFormatStyle, formatDateTime } from "@hopper-i18n/formatter";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";

dayjs.extend(customParseFormat);
dayjs.extend(utc);

export const formatPhoneHours = (hours: number, locale: string) => {
  // create a new UTC time but set the hours to the input
  const newUtcTime = dayjs().utc().set("hour", hours);
  // convert the UTC time to the browser's local time
  const localHours = newUtcTime.local().toDate();

  return formatDateTime(
    localHours,
    locale,
    DateTimeFormatStyle.Custom({ hour: "numeric", hour12: true }) // e.g. 7 AM
  );
};

export const getLocalTimeZoneCode = () => {
  const localNow = new Date().toLocaleTimeString(undefined, {
    timeZoneName: "short",
  });

  // Example: "12:00:00 PM EDT"
  // Destructure with variables: [timeStr, meridiem, timeZoneCode] = ["12:00:00", "PM", "EDT"]
  const [, , timeZoneCode = ""] = localNow.split(" ");

  return timeZoneCode;
};

export function getPhoneServiceHours(
  phoneAvailability?: PhoneSupportAvailability,
  bookingType?: BookingType
) {
  let serviceStartUtc = 0;
  let serviceEndUtc = 24;

  if (phoneAvailability) {
    const { air, general, ground, hotel } = phoneAvailability;

    if (bookingType == null && general) {
      ({ serviceStartUtc, serviceEndUtc } = general);
    } else if (bookingType === BookingType.Flight && air) {
      ({ serviceStartUtc, serviceEndUtc } = air);
    } else if (bookingType === BookingType.Ground && ground) {
      ({ serviceStartUtc, serviceEndUtc } = ground);
    } else if (bookingType === BookingType.Lodging && hotel) {
      ({ serviceStartUtc, serviceEndUtc } = hotel);
    }
  }

  return [serviceStartUtc, serviceEndUtc];
}

/**
 * A Core version of the util found in libs/chat
 * @param startHour The start hour of the service window in UTC (0-23)
 * @param endHour The end hour of the service window in UTC (0-24)
 * @returns True if the current UTC time is within the service window, false otherwise
 */
export function isWithinServiceHours(startHour: number, endHour: number) {
  const currentUtcHour = dayjs().utc().hour();

  if (startHour > endHour) {
    return currentUtcHour >= startHour || currentUtcHour < endHour;
  }

  return currentUtcHour >= startHour && currentUtcHour < endHour;
}
